import React, { useState, useEffect, useRef } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { useIntl, Link, changeLocale } from "gatsby-plugin-intl";
import { format as formatDate } from "date-fns";
import styled from "styled-components";
// IMPORTANT: Gatsbyのライフサイクルか何かの原因で@エイリアスが効かないので、ここの記法はWarningを無視する
import { sizes } from "../../constants/sizes";
import DateTimeFormatter from "../../utils/dateTimeFormatter";
import CustomImage from "../../components/common/image";
import { images } from "../../constants/mediaPath";
import {
  CmsLanguages,
  CmsResponseData,
  ClassificationType,
  InfotmationType,
} from "../../types/informations";
import {getMicroCMSCategoryIntl} from "../../utils/getIntlMessage";

export const cmsLanguagesWithKey: Record<string, CmsLanguages> = {
  en: "英語",
  "zh-cn": "中国語(簡体字)",
  "zh-tw": "中国語(繁体字)",
  ko: "韓国語",
};

const StyledInfomations = styled.ul`
  li {
    padding-bottom: 10px;
    margin-bottom: 15px;
    border-bottom: solid 1px #dddddd;
    @media (max-width: ${sizes.breakPoint}px) {
      padding-bottom: 12px;
      margin-bottom: 12px;
    }
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const StyledInfomation = styled.div<{ $isHighlight?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding: 20px 0 14px 20px;

  @media (max-width: ${sizes.breakPoint}px) {
    //align-items: flex-start;
    padding: 10px 14px;
    gap: 0.5em;
  }

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    height: 100%;
    background: ${({ $isHighlight }) =>
      $isHighlight ? "var(--color-beige-3)" : "transparent"};
    content: "";
  }
`;

const InfoDate = styled.div`
  font-size: 15px;
  font-weight: 400;
  white-space: nowrap;
    display: flex;
    flex-direction: row;
    align-items: center;
  @media (max-width: ${sizes.breakPoint}px) {
    font-size: 12px;
  }
`;

const CategoryLabel = styled.div`
  display: inline-block;
    font-size: 14px;
    background-color: var(--color-beige-2);
    color: #ffffff;
    padding: 0 8px;
    margin-left: 13px;
    @media (max-width: ${sizes.breakPoint}px) {
        margin-left: 8px;
        font-size: 12px;
    }
`;

const NewsItem = ({ itemData }: { itemData: InfotmationType }) => {
  const intl = useIntl();
  return (
    <StyledInfomation $isHighlight={itemData.recommend}>
      <InfoDate>
        <DateTimeFormatter date={itemData.publishedAt} />
        {itemData.category && itemData.category.length > 0 && <CategoryLabel>{
          getMicroCMSCategoryIntl({intl, categoryName:itemData.category[0]})}</CategoryLabel>}
      </InfoDate>
      <p>{itemData.title}</p>
    </StyledInfomation>
  );
};

export const CNewsList = ({ data }: { data: CmsResponseData[] }) => {
  // console.log(data);
  return (
    <StyledInfomations>
      {data.map(({ node }) => (
        <li key={node.id}>
          {node.pdf || node.link?.href ? (
            <a
              href={node.pdf?.url || node.link?.href}
              target={
                node.pdf?.url || (node.link?.href && node.link?.blank)
                  ? "_blank"
                  : "_self"
              }
              rel={
                node.pdf?.url || (node.link?.href && node.link?.blank)
                  ? "_blank"
                  : "_self"
              }
            >
              <NewsItem itemData={node} />
            </a>
          ) : (
            <Link to={`/latest-update/${node.lang_id}/`}>
              <NewsItem itemData={node} />
            </Link>
          )}
        </li>
      ))}
    </StyledInfomations>
  );
};

const PagingList = styled.ul`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin-top: 50px;
  .btn.prev {
    margin-right: 24px;
  }
  .btn.next {
    margin-left: 24px;
  }
  > li {
    width: 48px;
    height: 48px;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    > a {
      width: 100%;
      text-align: center;
    }
    &.is_current {
      picture {
        position: absolute;
      }
      //background: ${($currentSrc) => `url(${$currentSrc})`};
    }
    > a {
      text-decoration: none;
    }
  }
`;

export const CPagingList = ({
  exClass,
  totalCount,
  path,
  perPage,
  current,
}: any) => {
  const isSp = useMobile();
  const page = Math.ceil(totalCount / perPage);
  const prevPath = current == 2 ? path : `${path}page/${current - 1}/`;
  return page > 1 ? (
    <PagingList className={`${exClass || ""}`}>
      <li className="btn prev">
        {current <= 1 ? (
          <span>
            {/*<i className="icon-arrow">＜</i>*/}
            <CustomImage
              src={images.en.common.paginationCirclePrev}
              aspectRatio="1"
            />
          </span>
        ) : (
          <Link to={prevPath}>
            <CustomImage
              src={images.en.common.paginationCirclePrev}
              aspectRatio="1"
            />
          </Link>
        )}
      </li>
      {isSp ? (
        <li className="total">
          {current > 9 ? current : "0" + current}
          <span>/{page > 9 ? page : "0" + page}</span>
        </li>
      ) : (
        <>
          {[...Array(page)].map((_, index) => {
            const pageNumber = index + 1;
            const pagePath = index == 0 ? path : `${path}page/${pageNumber}/`;
            return (
              <li
                className={`item ${current == pageNumber ? "is_current" : ""}`}
                key={index}
              >
                {current == pageNumber && (
                  <CustomImage
                    src={images.en.common.paginationCircleCurrent}
                    aspectRatio="1"
                  />
                )}
                <Link to={pagePath}>{pageNumber}</Link>
              </li>
            );
          })}
        </>
      )}
      <li className="btn next">
        {current >= page ? (
          <span>
            <CustomImage
              src={images.en.common.paginationCircleNext}
              aspectRatio="1"
            />
          </span>
        ) : (
          <Link to={`${path}page/${current + 1}/`}>
            <CustomImage
              src={images.en.common.paginationCircleNext}
              aspectRatio="1"
            />
          </Link>
        )}
      </li>
    </PagingList>
  ) : (
    <></>
  );
};

export const PeriodText = ({
  start,
  end,
}: {
  start?: string;
  end?: string;
}) => {
  const intl = useIntl();
  return (
    <>
      {intl.formatMessage({ id: "common.period" })}：
      {start && <DateTimeFormatter date={start} />}
      {start && end && start !== end && " 〜 "}
      {end && start !== end && <DateTimeFormatter date={end} />}
    </>
  );
};

const useMobile = (): boolean => {
  const [devise, setDevice] = useState(false);
  useEffect(() => {
    const update = () => {
      if (window.innerWidth > 768) {
        setDevice(false);
      } else {
        setDevice(true);
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  });
  return devise;
};

export const infoChoice = (
  data: CmsResponseData[] = [],
  classification: ClassificationType[] = [],
  count = 3,
  skip = 0,
  important = false
) => {
  // console.log(`[infoChoice]: data,`, data)
  const ary = data.filter((item) => {
    //終了日除外
    const currentDate = new Date();
    currentDate.setHours(0);
    currentDate.setMinutes(0);
    const eventEndDate = new Date(item.node.date_range?.end || "");
    eventEndDate.setDate(eventEndDate.getDate());
    eventEndDate.setHours(23);
    eventEndDate.setMinutes(59);
    if (item.node.date_range?.end && currentDate > eventEndDate) {
      return false;
    }

    //区分フィルター
    if (classification.length > 0) {
      if (!item.node.classification.some((el) => classification.includes(el))) {
        return false;
      }
    }

    //重要なお知らせフィルター
    if (important && !item.node.important) {
      return false;
    }
    return true;
  });
  return ary.slice(skip, count == 0 ? undefined : count);
};

export const infoGet = () => {
  const newsdataquery = useStaticQuery(graphql`
    query {
      allMicrocmsInfoRphMultilingual(sort: { sortIndex: ASC }) {
        edges {
          node {
            id
            pdf {
              url
            }
            title
            publishedAt
            link {
              href
              blank
            }
            date_range {
              start
              end
            }
            classification
            category
            recommend
            infoRphMultilingualId
            lang
            lang_id
            important
          }
        }
      }
    }
  `);
  const intl = useIntl();
  const intllocate = intl.locale;
  const cmsLanguage = cmsLanguagesWithKey[intllocate];
  const newsdata = newsdataquery.allMicrocmsInfoRphMultilingual.edges.filter(
    (item: CmsResponseData) => {
      //言語フィルター
      if (!item.node.lang.includes(cmsLanguage)) {
        // console.error(`cmsLanguage: ${cmsLanguage} != ${item.node.lang}`)
        return false;
      }
      return true;
    }
  );
  // console.warn(`newsdata length: ${newsdata.length}`)
  return newsdata;
};
